import { Link, useNavigate } from "react-router-dom";
import {
  BookingData,
  getBookingStatusColor,
  getBookingTypeText,
  formatDateRange,
} from "../utils/bookingUtils";
import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  BookingStatus,
  PaymentStatus,
} from "../constants-generated/well-known-values";

export default function BookingCard(booking: BookingData) {
  const navigate = useNavigate();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  console.log("🖼️ BookingCard Image URL:", booking.workspaceImageUrl);
  console.log("🖼️ Full Booking Object:", booking);

  // Check if conversation exists for this booking
  useEffect(() => {
    const checkConversation = async () => {
      try {
        const token = Cookies.get("authToken");
        const response = await axios.get(
          `/api/chat/by-booking/${booking.id}?userId=${booking.userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.success && response.data.conversationId) {
          setConversationId(response.data.conversationId);
        }
      } catch {
        // No conversation exists yet, that's fine
      }
    };
    checkConversation();
  }, [booking.id, booking.userId]);

  const handleConversationClick = async () => {
    setIsLoadingConversation(true);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.get(
        `/api/chat/by-booking/${booking.id}?userId=${booking.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success && response.data.conversationId) {
        navigate(`/messages?conversation=${response.data.conversationId}`);
      } else {
        toast.error(response.data.message || "Failed to access conversation");
      }
    } catch (error) {
      console.error("Error accessing conversation:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const handleCompletePayment = async () => {
    setIsProcessingPayment(true);
    try {
      const token = Cookies.get("authToken");
      const response = await axios.post(
        "/api/payment/create-checkout-session",
        {
          userId: booking.userId,
          workspaceId: booking.workspaceId,
          startDate: booking.startDate,
          endDate: booking.endDate,
          totalPrice: booking.totalPrice,
          message: `Payment for booking #${booking.id}`,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        toast.error(
          response.data.message || "Failed to create payment session"
        );
      }
    } catch (error) {
      console.error("Error creating payment session:", error);
      toast.error(
        "An error occurred while processing payment. Please try again."
      );
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 h-full flex flex-col border border-gray-200">
      {/* Workspace Image */}
      <div className="aspect-w-16 aspect-h-9 overflow-hidden">
        <img
          src={booking.workspaceImageUrl || "/placeholder-workspace.jpg"}
          alt={booking.workspaceTitle}
          className="w-full h-48 object-cover"
          onError={(e) => {
            console.error(
              "❌ Image failed to load:",
              booking.workspaceImageUrl
            );
            e.currentTarget.src = "/placeholder-workspace.jpg";
          }}
          onLoad={() => {
            console.log(
              "✅ Image loaded successfully:",
              booking.workspaceImageUrl
            );
          }}
        />
      </div>

      <div className="p-6 flex-grow flex flex-col">
        {/* Booking Status and Type */}
        <div className="flex justify-between items-start mb-3">
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${getBookingStatusColor(
              booking.status
            )}`}
          >
            {booking.status}
          </span>
          <span className="bg-blue-50 text-[var(--secondary)] text-xs font-medium px-2 py-1 rounded">
            {getBookingTypeText(booking.bookingType)}
          </span>
        </div>

        {/* Workspace Title */}
        <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
          {booking.workspaceTitle}
        </h3>

        {/* Address */}
        <p className="text-gray-600 mb-3 text-sm line-clamp-2">
          {booking.workspaceAddress}, {booking.workspaceCity},{" "}
          {booking.workspaceState}
        </p>

        {/* Booking Dates */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">Booking Period:</span>
          </div>
          <p className="text-sm text-gray-900 mt-1 font-medium">
            {formatDateRange(booking.startDate, booking.endDate)}
          </p>
        </div>

        {/* Pricing and Details */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <span className="text-lg font-bold text-gray-900">
              ${booking.totalPrice.toLocaleString()}
            </span>
            <span className="text-sm text-gray-500 ml-1">total</span>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Capacity</div>
            <div className="font-medium text-gray-900">
              {booking.workspaceCapacity} people
            </div>
          </div>
        </div>

        {/* Workspace Type */}
        <div className="mb-4">
          <span className="bg-indigo-100 text-indigo-800 text-xs font-medium px-2.5 py-1 rounded">
            {booking.workspaceType}
          </span>
          <span className="text-gray-500 text-xs ml-2">
            {booking.workspaceSizeSqm} sqm
          </span>
        </div>

        {/* Message if exists */}
        {booking.message && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <svg
                className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-yellow-800 text-sm">{booking.message}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-auto space-y-3">
          <Link
            to={`/workspace/${booking.workspaceId}`}
            className="block w-full bg-[var(--secondary)] hover:bg-blue-700 text-white text-center font-medium py-3 px-4 rounded-lg transition-colors duration-300"
          >
            View Workspace
          </Link>

          {/* Conversation Button */}
          <button
            onClick={handleConversationClick}
            disabled={isLoadingConversation}
            className="flex items-center justify-center gap-2 w-full bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors duration-300 border border-gray-300"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            {isLoadingConversation
              ? "Loading..."
              : conversationId
              ? "View Conversation"
              : "Start Conversation"}
          </button>

          {/* Complete Payment Button - Only show for confirmed bookings with incomplete payment */}
          {booking.status === BookingStatus.Confirmed &&
            booking.paymentStatus === PaymentStatus.Incomplete && (
              <button
                onClick={handleCompletePayment}
                disabled={isProcessingPayment}
                className="block w-full bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-center font-medium py-3 px-4 rounded-lg transition-colors duration-300"
              >
                {isProcessingPayment ? "Processing..." : "Complete Payment"}
              </button>
            )}
        </div>
      </div>
    </div>
  );
}
